import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { CheckCircle, AlertCircle, Loader2, Upload, FileImage, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function RejectArtwork() {
  const { token } = useParams();
  const [status, setStatus] = useState('form'); // form, submitting, success, error
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setFilePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!notes.trim()) {
      setError('Please describe the changes you would like made.');
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      let fileData = null;
      
      // Convert file to base64 if uploaded
      if (file) {
        const reader = new FileReader();
        fileData = await new Promise((resolve) => {
          reader.onloadend = () => {
            // Remove the data URL prefix to get just base64
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(file);
        });
      }

      const response = await axios.post(`${API}/api/public/reject-artwork/${token}`, {
        notes: notes,
        file_data: fileData,
        file_name: file ? file.name : null
      });

      setStatus('success');
      setData(response.data);
    } catch (err) {
      setStatus('error');
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to submit your request. Please contact us directly.');
      setError(msg);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8">
          {status === 'form' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileImage className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Request Artwork Changes</h2>
                <p className="text-gray-600">
                  Please describe the changes you'd like made to your memorial design.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="notes" className="text-base font-semibold">
                    Describe the changes needed *
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Please describe what changes you would like made to the artwork. Be as specific as possible (e.g., 'Change the font on the name', 'Add a rose design in the corner', 'Fix the spelling of...')"
                    className="mt-2 min-h-[150px]"
                    required
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold">
                    Upload a reference image (optional)
                  </Label>
                  <p className="text-sm text-gray-500 mb-2">
                    You can mark up the design showing the changes you want, or upload a reference image.
                  </p>
                  
                  {!file ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        id="file-upload"
                        accept="image/*,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">Click to upload an image</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, PDF up to 10MB</p>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {filePreview ? (
                            <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded" />
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                              <FileImage className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-800">{file.name}</p>
                            <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={removeFile}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" size="lg">
                  Submit Change Request
                </Button>
              </form>
            </div>
          )}

          {status === 'submitting' && (
            <div className="text-center py-8">
              <Loader2 className="w-16 h-16 text-amber-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800">Submitting your request...</h2>
              <p className="text-gray-600 mt-2">Please wait a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">Request Submitted!</h2>
              <p className="text-gray-600 mb-4">
                Thank you, {data?.client_name}! We've received your change request for order <strong>#{data?.order_number}</strong>.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                <h3 className="font-semibold text-green-800 mb-2">What happens next?</h3>
                <ul className="text-green-700 text-sm space-y-2">
                  <li>✓ Our team has been notified</li>
                  <li>✓ We'll review your requested changes</li>
                  <li>✓ We'll update the artwork and send you a new design</li>
                  <li>✓ You'll receive another email to approve the updated design</li>
                </ul>
              </div>
              <p className="text-gray-500 text-sm mt-6">
                If you have any questions, please contact us.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-700 mb-2">Something Went Wrong</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => setStatus('form')} variant="outline">
                Try Again
              </Button>
              <p className="text-gray-500 text-sm mt-4">
                Or contact us at <a href="mailto:memorials@setinstonedidcot.co.uk" className="text-blue-600 underline">memorials@setinstonedidcot.co.uk</a>
              </p>
            </div>
          )}

          <div className="mt-8 pt-6 border-t text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_headstone-orders/artifacts/1irdopez_email%20logo.png" 
              alt="Set In Stone Didcot Ltd" 
              className="h-12 mx-auto mb-2"
            />
            <p className="text-gray-500 text-sm">Set In Stone Didcot Ltd</p>
            <p className="text-gray-400 text-xs">Memorial Specialists</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default RejectArtwork;
